import axiosInstance from "../Instance/axiosInstance";

export interface CreateDiscountPayload {
  discountName: string;
  description?: string;
  startDate?: string;
  endDate: string;
  valueType: "percentage" | "flat" | "flatAmount";
  entityType: "buyer" | "seller" | "category";
  entityIds: number[]; // Array of entity IDs
  entityNames?: string[]; // Optional, backend will populate
  minOrderValue?: number | null;
  maxDiscount?: number | null;
}

export interface DiscountListItem {
  id: number;
  discountName: string;
  description: string | null;
  startDate: string;
  endDate: string;
  valueType: "percentage" | "flat" | "flatAmount";
  entityType: "buyer" | "seller" | "category";
  entityIds: number[]; // Array of entity IDs
  entityNames: string[]; // Array of entity names
  entityId?: number; // For backward compatibility
  entityName?: string; // For backward compatibility
  minOrderValue: number | null;
  maxDiscount: number | null;
  isActive: boolean;
  isDelete: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ApiResponse {
  success: boolean;
  message: string;
  data?: any;
}

// Create Discount
export const createDiscount = async (payload: CreateDiscountPayload): Promise<ApiResponse> => {
  try {
    const { data } = await axiosInstance.post("/discount/add", payload);
    return data;
  } catch (error: any) {
    throw error?.response?.data || error;
  }
};

// Update Discount
export const updateDiscount = async (id: number, payload: CreateDiscountPayload): Promise<ApiResponse> => {
  try {
    const { data } = await axiosInstance.put(`/discount/update/${id}`, payload);
    return data;
  } catch (error: any) {
    throw error?.response?.data || error;
  }
};

// Update Discount Status
export const updateDiscountStatus = async (id: number, isActive: boolean): Promise<ApiResponse> => {
  try {
    const { data } = await axiosInstance.put(`/discount/status/${id}`, { isActive });
    return data;
  } catch (error: any) {
    throw error?.response?.data || error;
  }
};

// Delete Discount (Soft Delete)
export const deleteDiscount = async (id: number): Promise<ApiResponse> => {
  try {
    const { data } = await axiosInstance.delete(`/discount/delete/${id}`);
    return data;
  } catch (error: any) {
    throw error?.response?.data || error;
  }
};

// Get Discount List
export const getDiscountList = async (): Promise<DiscountListItem[]> => {
  try {
    const response = await axiosInstance.get("/discount/list");
    const list = response?.data?.data;
    
    // Ensure entityIds and entityNames are always arrays
    if (Array.isArray(list)) {
      return list.map((item: any) => ({
        ...item,
        entityIds: Array.isArray(item.entityIds) ? item.entityIds : [],
        entityNames: Array.isArray(item.entityNames) ? item.entityNames : [],
      }));
    }
    
    return [];
  } catch (error: any) {
    console.error("Error fetching discount list:", error);
    throw error?.response?.data || error;
  }
};

// Get Single Discount by ID
export const getDiscountById = async (id: number): Promise<DiscountListItem> => {
  try {
    const { data } = await axiosInstance.get(`/discount/${id}`);
    const item = data?.data;
    
    return {
      ...item,
      entityIds: Array.isArray(item.entityIds) ? item.entityIds : [],
      entityNames: Array.isArray(item.entityNames) ? item.entityNames : [],
    };
  } catch (error: any) {
    throw error?.response?.data || error;
  }
};

// Helper function to get entity names from IDs (if needed)
export const getEntityNames = async (entityType: string, entityIds: number[]): Promise<string[]> => {
  try {
    const { data } = await axiosInstance.post("/discount/get-entity-names", {
      entityType,
      entityIds,
    });
    return data?.data?.entityNames || [];
  } catch (error: any) {
    console.error("Error fetching entity names:", error);
    return [];
  }
};